import React from 'react';
import axios from 'axios';
import { Alert, Box, Breadcrumbs, CircularProgress, IconButton, Link, Paper, Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  AccessTime as AccessTimeIcon,
  Cached as CachedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  Folder as FolderIcon,
  HomeOutlined as HomeOutlinedIcon,
  HourglassTop as HourglassTopIcon,
  PersonOutline as PersonOutlineIcon,
  Slideshow as SlideshowIcon,
} from '@mui/icons-material';
import { useAuth } from 'context/auth-provider';
import { getCdtDisplayString } from '../../utils/util';

interface FileExplorerViewProps {
  isActive: boolean;
}

interface FileExplorerJob {
  job_id?: string;
  status?: string;
  client_id?: string;
  initiated_by?: string;
  end_time?: string;
  metadata?: {
    client_id?: string;
    output_key?: string;
    desired_output_filename?: string;
    file_name?: string;
  };
}

interface FileExplorerFile {
  key: string;
  jobId: string;
  fileName: string;
  status: string;
  generatedBy: string;
  creationTime: string;
  endTimeMs: number;
}

interface JobsApiResponse {
  jobs?: FileExplorerJob[];
  items?: FileExplorerJob[];
}

const toFileName = (outputKey?: string): string | null => {
  if (!outputKey) return null;
  const stripped = outputKey.startsWith('output/') ? outputKey.slice('output/'.length) : outputKey;
  return stripped || null;
};

const getDesiredOutputFileName = (job: FileExplorerJob): string | null => {
  const fileNameFromMetadata = job?.metadata?.file_name;
  if (typeof fileNameFromMetadata === 'string' && fileNameFromMetadata.trim()) {
    return fileNameFromMetadata.trim();
  }

  const desiredOutputFileName = job?.metadata?.desired_output_filename;
  if (typeof desiredOutputFileName === 'string' && desiredOutputFileName.trim()) {
    return desiredOutputFileName.trim();
  }

  const outputKeyFileName = toFileName(job?.metadata?.output_key);
  if (outputKeyFileName) {
    return outputKeyFileName;
  }

  return null;
};

const getGeneratedBy = (job: FileExplorerJob): string => {
  const value = job?.initiated_by|| '';
  return value.trim() || '-';
};

const getCreationTime = (job: FileExplorerJob): string => getCdtDisplayString(job?.end_time || '');

const getEndTimeMs = (job: FileExplorerJob): number => {
  const raw = (job?.end_time || '').trim();
  if (!raw) return Number.POSITIVE_INFINITY;
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

const buildClientFolders = (jobs: FileExplorerJob[]): Record<string, FileExplorerFile[]> => {
  const foldersMap: Record<string, FileExplorerFile[]> = {};

  jobs.forEach((job, index) => {
    const clientId = (job.client_id || job.metadata?.client_id || '').trim();
    if (!clientId) return;

    if (!foldersMap[clientId]) {
      foldersMap[clientId] = [];
    }

    const normalizedStatus = (job.status || '').toUpperCase();
    const fallbackJobId = (job.job_id || `job-${index + 1}`).trim();
    const jobId = (job.job_id || '').trim();
    const desiredOutputFileName = getDesiredOutputFileName(job);
    const completedFileName = toFileName(job.metadata?.output_key);

    const fileName = normalizedStatus === 'INPROGRESS'
      ? `${desiredOutputFileName || job.metadata?.file_name || fallbackJobId} [Processing PPT]`
      : desiredOutputFileName || completedFileName || `${fallbackJobId}.pptx`;

    foldersMap[clientId].push({
      key: `${fallbackJobId}-${index}`,
      jobId,
      fileName,
      status: normalizedStatus,
      generatedBy: getGeneratedBy(job),
      creationTime: getCreationTime(job),
      endTimeMs: getEndTimeMs(job),
    });
  });

  const result: Record<string, FileExplorerFile[]> = {};
  Object.keys(foldersMap)
    .sort((a, b) => a.localeCompare(b))
    .forEach((clientId) => {
      result[clientId] = foldersMap[clientId].sort((a, b) => {
        if (a.endTimeMs !== b.endTimeMs) return b.endTimeMs - a.endTimeMs;
        return a.fileName.localeCompare(b.fileName);
      });
    });

  return result;
};

export default function FileExplorerView({ isActive }: FileExplorerViewProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [clientFolders, setClientFolders] = React.useState<Record<string, FileExplorerFile[]>>({});
  const [activeClientId, setActiveClientId] = React.useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = React.useState<string | null>(null);
  const hasFetchedRef = React.useRef(false);

  const clientIdFromRoute = React.useMemo(() => {
    const state = location.state as { clientId?: string } | null;
    return typeof state?.clientId === 'string' && state.clientId.trim() ? state.clientId.trim() : null;
  }, [location.state]);

  const triggerBrowserDownload = React.useCallback((downloadUrl: string) => {
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDownloadClick = React.useCallback(async (file: FileExplorerFile) => {
    if (!user) {
      setErrorMessage('User not authenticated');
      return;
    }

    if (!file.jobId) {
      setErrorMessage('Unable to download: missing job id');
      return;
    }

    setErrorMessage(null);
    setDownloadingKey(file.key);

    try {
      const response = await axios.get('/get-download-url', {
        baseURL: process.env.REACT_APP_AMA_V2_API_URL,
        params: { jobId: file.jobId },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: user.idToken,
        },
      });

      const data = response?.data || {};
      const downloadUrl =
        (typeof data.download_url === 'string' && data.download_url) ||
        (typeof data.downloadUrl === 'string' && data.downloadUrl) ||
        (typeof data.url === 'string' && data.url) ||
        '';

      if (!downloadUrl) {
        setErrorMessage('Download URL was not returned by the server');
        return;
      }

      triggerBrowserDownload(downloadUrl);
    } catch (err) {
      console.error('FileExplorerView.handleDownloadClick :: error', err);
      if (axios.isAxiosError(err)) {
        const message =
          (typeof err.response?.data?.message === 'string' && err.response?.data?.message) || err.message;
        setErrorMessage(message || 'Failed to fetch download URL');
      } else {
        setErrorMessage('Failed to fetch download URL');
      }
    } finally {
      setDownloadingKey(null);
    }
  }, [triggerBrowserDownload, user]);

  const fetchJobs = React.useCallback(async () => {
    if (!user) {
      console.log('FileExplorerView.fetchJobs :: user not authenticated');
      setErrorMessage('Unable to load file explorer data. Please try again.');
      return;
    }

    setErrorMessage(null);
    setIsLoading(true);

    try {
      const response = await axios.get<JobsApiResponse>('/jobs', {
        baseURL: process.env.REACT_APP_AMA_V2_API_URL,
        params: { job_type: 'pdf_to_ppt_conversion' },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: user.idToken,
        },
      });

      console.log('FileExplorerView.fetchJobs :: /jobs response', response.data);

      const jobs = Array.isArray(response.data?.jobs)
        ? response.data.jobs
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];

      const folders = buildClientFolders(jobs);
      setClientFolders(folders);

      if (activeClientId && !folders[activeClientId]) {
        setActiveClientId(null);
      }
    } catch (err) {
      console.error('FileExplorerView.fetchJobs :: error', err);
      setErrorMessage('Unable to load file explorer data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [activeClientId, user]);

  React.useEffect(() => {
    if (!clientIdFromRoute) return;
    setActiveClientId(clientIdFromRoute);
  }, [clientIdFromRoute]);

  React.useEffect(() => {
    if (!isActive || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchJobs();
  }, [fetchJobs, isActive]);

  const folderIds = Object.keys(clientFolders);
  const currentFiles = activeClientId ? clientFolders[activeClientId] || [] : [];

  return (
    <Box sx={{ height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box>
          {!errorMessage && folderIds.length > 0 && (
          <Breadcrumbs
            aria-label="file explorer breadcrumb"
            sx={{
              mb: 0,
              '& .MuiTypography-root': { fontWeight: 700 },
              '& .MuiLink-root': { fontWeight: 700 },
            }}
          >
            {activeClientId ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                onClick={() => setActiveClientId(null)}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <HomeOutlinedIcon fontSize="small" />
                  <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                    Clients
                  </Typography>
                </Stack>
              </Link>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <HomeOutlinedIcon fontSize="small" color="primary" />
                <Typography color="text.primary">Clients</Typography>
              </Stack>
            )}
            {activeClientId && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <FolderIcon fontSize="small" color="primary" />
                <Typography color="text.primary">{activeClientId}</Typography>
              </Stack>
            )}
          </Breadcrumbs>
          )}
        </Box>
        <IconButton
          aria-label="Refresh File Explorer Jobs"
          title="Refresh"
          onClick={fetchJobs}
          sx={{ color: 'primary.main' }}
        >
          <CachedIcon sx={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
        </IconButton>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {errorMessage}
        </Alert>
      )}

      {!errorMessage && !isLoading && folderIds.length === 0 && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          No data to show.
        </Typography>
      )}

      {!errorMessage && folderIds.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {!activeClientId && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {folderIds.map((clientId) => (
                <Paper
                  key={clientId}
                  variant="outlined"
                  onDoubleClick={() => setActiveClientId(clientId)}
                  sx={{
                    width: 180,
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FolderIcon color="primary" />
                    <Typography variant="body2" noWrap title={clientId} sx={{ userSelect: 'none' }}>
                      {clientId}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}

          {activeClientId && (
            <Box>
              {currentFiles.length === 0 ? (
                <Typography variant="body1" color="text.secondary">
                  No PPT files found.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {currentFiles.map((file) => (
                    <Paper key={file.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          {file.status === 'INPROGRESS' ? (
                            <HourglassTopIcon color="warning" />
                          ) : file.status === 'COMPLETED' ? (
                            <SlideshowIcon color="primary" />
                          ) : (
                            <SlideshowIcon color="action" />
                          )}
                          <Typography variant="body2" noWrap title={file.fileName}>
                            {file.fileName}
                          </Typography>
                          {file.status === 'COMPLETED' && (
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                void handleDownloadClick(file);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  void handleDownloadClick(file);
                                }
                              }}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                color: 'primary.main',
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                  color: 'primary.dark',
                                },
                              }}
                            >
                              {downloadingKey === file.key ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <>
                                  <DownloadOutlinedIcon fontSize="small" />
                                  <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 500 }}>
                                    Download
                                  </Typography>
                                </>
                              )}
                            </Box>
                          )}
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PersonOutlineIcon fontSize="inherit" />
                            <Typography variant="caption" color="text.secondary">
                              {file.generatedBy}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon fontSize="inherit" />
                            <Typography variant="caption" color="text.secondary">
                              {file.creationTime}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
